import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { errorMessage, type ActivityDuplicateRef } from "@atlas/shared";
import { keepBothCopies } from "../api/activities";

// Spec 37: copies of this walk from other sources that Atlas parked as
// duplicates. One quiet line each, with "Keep both instead" as the undo.

const SOURCE_LABELS: Record<string, string> = {
  strava: "Strava",
  komoot: "Komoot",
  apple_health: "Apple Health",
  health_connect: "Health Connect",
  garmin: "Garmin",
  gpx: "a GPX upload",
};

function DuplicateLine({ activityId, dup }: { activityId: string; dup: ActivityDuplicateRef }) {
  const queryClient = useQueryClient();
  const keepBoth = useMutation({
    mutationFn: () => keepBothCopies(activityId, dup.exclusionId),
    onSuccess: () => {
      // The restored copy comes back to the list and is reprocessed; trips can change too.
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      void queryClient.invalidateQueries({ queryKey: ["trips"] });
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
  const source = SOURCE_LABELS[dup.sourceType] ?? dup.sourceType;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, color: "var(--text-secondary)" }}>
      <Copy size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
      <span>Also synced from {source}. Kept this copy.</span>
      <button
        type="button"
        disabled={keepBoth.isPending}
        onClick={() => keepBoth.mutate()}
        style={{
          padding: 0,
          border: "none",
          background: "none",
          font: "inherit",
          color: "var(--text-accent)",
          cursor: keepBoth.isPending ? "wait" : "pointer",
        }}
      >
        {keepBoth.isPending ? "Restoring…" : "Keep both instead"}
      </button>
      {keepBoth.isError && (
        <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
          {errorMessage(keepBoth.error, "Couldn't restore the other copy. Try again.")}
        </span>
      )}
    </div>
  );
}

export function ActivityDuplicates({ activityId, duplicates }: { activityId: string; duplicates: ActivityDuplicateRef[] | undefined }) {
  if (!duplicates || duplicates.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {duplicates.map((d) => (
        <DuplicateLine key={d.exclusionId} activityId={activityId} dup={d} />
      ))}
    </div>
  );
}
